import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { RepositoryIdentity, ScopedProjectRef } from "@cmmd-forge/contracts";
import type { Project } from "./types";
import { DEFAULT_PROJECT_GROUPING_MODE, type ProjectGroupingMode } from "./projectGrouping";

/**
 * Compose the canonical-key portion of a logical project key.
 *
 * The bare canonical key (normalized git remote URL) only identifies the
 * repository — not WHICH part of the repository the project's cwd points
 * at. For monorepos, two projects can share the same remote URL while
 * pointing at completely different packages (e.g. `apps/server` vs
 * `apps/web`); collapsing them by canonical key alone would silently merge
 * unrelated projects. So we suffix the in-repo subpath when present.
 *
 * Two checkouts of the SAME repo at the SAME in-repo path (e.g. three
 * worktrees of cmmd-forge all registered at `apps/server`) share the
 * resulting key and are correctly grouped as one logical project.
 */
function composeRepositoryLogicalKey(
  identity: RepositoryIdentity,
  mode: ProjectGroupingMode = DEFAULT_PROJECT_GROUPING_MODE,
): string {
  // Grouping by repository drops the subpath entirely, which lands on the BARE
  // canonical key — the same string the project route uses. Any other shape
  // would group the sidebar correctly and then fail to resolve when clicked.
  if (mode === "repository") return identity.canonicalKey;

  const subpath = identity.repositorySubpath?.trim();
  return subpath && subpath.length > 0
    ? `${identity.canonicalKey}#${subpath}`
    : identity.canonicalKey;
}

export function deriveLogicalProjectKey(
  project: Pick<Project, "environmentId" | "id" | "repositoryIdentity">,
  mode: ProjectGroupingMode = DEFAULT_PROJECT_GROUPING_MODE,
): string {
  if (project.repositoryIdentity) {
    return composeRepositoryLogicalKey(project.repositoryIdentity, mode);
  }
  return scopedProjectKey(scopeProjectRef(project.environmentId, project.id));
}

export function deriveLogicalProjectKeyFromRef(
  projectRef: ScopedProjectRef,
  project: Pick<Project, "repositoryIdentity"> | null | undefined,
): string {
  if (project?.repositoryIdentity) return composeRepositoryLogicalKey(project.repositoryIdentity);
  return scopedProjectKey(projectRef);
}

function normalizeCwdKey(cwd: string): string {
  return cwd.replace(/\\/g, "/").replace(/\/+$/, "").toLowerCase();
}

/**
 * Path-shape grouping fallback for worktrees that can't be canonically
 * identified (e.g. the on-disk folder is gone, so `git rev-parse` fails
 * and the project arrives with `repositoryIdentity = null`).
 *
 * Detects the conventions:
 * - `<host>/[.]worktrees/<repo>/<worktree>/<subpath?>`
 * - `<host>/.codex/worktrees/<slug>/<repo>/<subpath?>`
 * - `<host>/<repo>/.claude/worktrees/<slug>/<subpath?>`
 * - `<host>/conductor/workspaces/<repo>/<workspace>/<subpath?>`
 *
 * The first shape is used by Cursor's
 * `~/.cursor/worktrees/<repo>/<name>` and hand-rolled
 * `<project>/.worktrees/<repo>/<name>` layouts. Codex uses the second
 * shape (`~/.codex/worktrees/<slug>/<repo>`), where the short slug comes
 * before the repository folder. Both derive a stable key of the form
 * `worktree-shape:<repo>#<subpath>`. Two checkouts of the same repo at
 * the same in-repo subpath share the resulting key even when one of them
 * is missing on disk.
 *
 * Returns `null` for paths that don't match the convention.
 */
export function deriveWorktreePathShapeKey(cwd: string | null | undefined): string | null {
  if (!cwd) return null;
  const normalized = cwd.replace(/\\/g, "/").replace(/\/+$/, "");
  const segments = normalized.split("/").filter((segment) => segment.length > 0);

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (segments[i]?.toLowerCase() !== "conductor") continue;
    if (segments[i + 1]?.toLowerCase() !== "workspaces") continue;
    const repo = segments[i + 2];
    const workspace = segments[i + 3];
    if (!repo || !workspace) return null;
    const subpath = segments.slice(i + 4).join("/");
    return `worktree-shape:${repo.toLowerCase()}#${subpath.toLowerCase()}`;
  }

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (segments[i]?.toLowerCase() !== ".claude") continue;
    if (segments[i + 1]?.toLowerCase() !== "worktrees") continue;
    const repo = segments[i - 1];
    const worktree = segments[i + 2];
    if (!repo || !worktree) return null;
    const subpath = segments.slice(i + 3).join("/");
    return `worktree-shape:${repo.toLowerCase()}#${subpath.toLowerCase()}`;
  }

  // Find the LAST `worktrees` (or `.worktrees`) segment so paths like
  // `.../worktrees/.../worktrees/...` resolve to the inner-most checkout
  // group. This also tolerates repos that happen to contain a
  // `worktrees/` directory at higher levels.
  let worktreesIndex = -1;
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (segment === "worktrees" || segment === ".worktrees") {
      worktreesIndex = i;
      break;
    }
  }
  // Need: <worktrees>/<repo>/<worktree-name>[/<subpath>] for Cursor/Forge
  // or <.codex>/<worktrees>/<slug>/<repo>[/<subpath>] for Codex.
  if (worktreesIndex === -1 || worktreesIndex + 2 >= segments.length) return null;
  const isCodexWorktree =
    worktreesIndex > 0 && segments[worktreesIndex - 1]?.toLowerCase() === ".codex";
  const repo = isCodexWorktree ? segments[worktreesIndex + 2] : segments[worktreesIndex + 1];
  if (!repo) return null;
  const subpathSegments = segments.slice(worktreesIndex + 3);
  const subpath = subpathSegments.join("/");
  return `worktree-shape:${repo.toLowerCase()}#${subpath.toLowerCase()}`;
}

/**
 * Derive the worktree shape that belongs to an identity-bearing normal
 * checkout. The repository subpath is source-of-truth metadata, so removing
 * it from the cwd recovers the local checkout folder without guessing where
 * the repository root lives.
 *
 * This alias lets a canonical checkout such as
 * `/projects/cmmd-forge/apps/server` claim the same shape as a stale
 * `/conductor/workspaces/cmmd-forge/<workspace>/apps/server` record. Callers
 * should prefer `deriveWorktreePathShapeKey` for recognized worktree paths.
 */
export function deriveRepositoryCheckoutPathShapeKey(
  cwd: string | null | undefined,
  repositorySubpath: string | null | undefined,
): string | null {
  if (!cwd) return null;

  const cwdSegments = cwd
    .replace(/\\/g, "/")
    .replace(/\/+$/, "")
    .split("/")
    .filter((segment) => segment.length > 0);
  const subpathSegments = (repositorySubpath ?? "")
    .replace(/\\/g, "/")
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter((segment) => segment.length > 0 && segment !== ".");

  if (subpathSegments.length > cwdSegments.length) return null;
  const suffixStart = cwdSegments.length - subpathSegments.length;
  const hasMatchingSuffix = subpathSegments.every(
    (segment, index) => cwdSegments[suffixStart + index]?.toLowerCase() === segment.toLowerCase(),
  );
  if (!hasMatchingSuffix) return null;

  const checkoutSegments = cwdSegments.slice(0, suffixStart);
  const repo = checkoutSegments.at(-1);
  if (!repo) return null;

  return `worktree-shape:${repo.toLowerCase()}#${subpathSegments
    .map((segment) => segment.toLowerCase())
    .join("/")}`;
}

/**
 * Resolve every physical project to the same logical group used by the sidebar.
 * Missing worktrees inherit an identity-bearing checkout only when the path
 * shape has exactly one canonical claimant.
 */
export function deriveLogicalProjectKeyMap(
  projects: readonly Project[],
  /**
   * How each repository should be grouped. Asked per REPOSITORY, not per
   * project, so every checkout of one repo necessarily gets the same answer —
   * two disagreeing would split a group in half.
   */
  modeFor: (identity: RepositoryIdentity) => ProjectGroupingMode = () =>
    DEFAULT_PROJECT_GROUPING_MODE,
): ReadonlyMap<string, string> {
  const candidates = new Map<string, Set<string>>();
  for (const project of projects) {
    if (!project.repositoryIdentity) continue;
    const shape =
      deriveWorktreePathShapeKey(project.cwd) ??
      deriveRepositoryCheckoutPathShapeKey(
        project.cwd,
        project.repositoryIdentity.repositorySubpath,
      );
    if (!shape) continue;
    const keys = candidates.get(shape) ?? new Set<string>();
    keys.add(deriveLogicalProjectKey(project, modeFor(project.repositoryIdentity)));
    candidates.set(shape, keys);
  }

  const pathShapeToCanonicalKey = new Map<string, string>();
  for (const [shape, keys] of candidates) {
    if (keys.size !== 1) continue;
    const key = keys.values().next().value;
    if (key) pathShapeToCanonicalKey.set(shape, key);
  }

  const result = new Map<string, string>();
  for (const project of projects) {
    const physicalKey = scopedProjectKey(scopeProjectRef(project.environmentId, project.id));
    const shape = project.repositoryIdentity ? null : deriveWorktreePathShapeKey(project.cwd);
    result.set(
      physicalKey,
      (shape ? pathShapeToCanonicalKey.get(shape) : undefined) ??
        deriveLogicalProjectKey(
          project,
          project.repositoryIdentity ? modeFor(project.repositoryIdentity) : undefined,
        ),
    );
  }
  return result;
}

/**
 * Sidebar-specific grouping key.
 *
 * Preference order:
 * 1. Repository canonical key + in-repo subpath (cross-environment grouping
 *    that respects monorepo boundaries — see `composeRepositoryLogicalKey`).
 * 2. Environment + normalized cwd (dedupe accidental duplicate project rows
 *    when no repository identity is available).
 * 3. Scoped project key (strict identity fallback).
 */
export function deriveSidebarLogicalProjectKey(
  project: Pick<Project, "environmentId" | "id" | "cwd" | "repositoryIdentity">,
): string {
  if (project.repositoryIdentity) return composeRepositoryLogicalKey(project.repositoryIdentity);

  const cwd = project.cwd?.trim();
  const worktreeShape = deriveWorktreePathShapeKey(cwd);
  if (worktreeShape) return worktreeShape;

  if (cwd) return `env:${project.environmentId}:cwd:${normalizeCwdKey(cwd)}`;

  return scopedProjectKey(scopeProjectRef(project.environmentId, project.id));
}
